/**
 * The till.
 *
 * Layout follows the mockup: a search bar and view toggle, the order-type and
 * table and customer chips on the right of it, the category strip, the item
 * grid, and the order panel down the right-hand side. The function-key strip
 * runs along the bottom.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FiSearch, FiUsers, FiUser, FiPlus, FiHelpCircle, FiPause,
    FiClock, FiGrid, FiList, FiPrinter, FiChevronDown, FiShoppingCart,
    FiX
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import CategoryTabs from '../components/pos/CategoryTabs';
import ItemGrid from '../components/pos/ItemGrid';
import OrderPanel from '../components/pos/OrderPanel';
import ModifierModal, { itemNeedsDialog } from '../components/pos/ModifierModal';
import PaymentModal from '../components/pos/PaymentModal';
import {
    HoldDialog, RecallDialog, DiscountDialog, NoteDialog,
    CustomerDialog, TableDialog, LineOptionsDialog
} from '../components/pos/PosDialogs';

import {
    addItem, clearCart, loadFromOrder, setOrderType, selectCart,
    selectTotals, selectOrderPayload, setSaving
} from '../store/cartSlice';
import {
    setSearch, selectMenu, selectOrderTypes, selectPaymentModes, setViewMode, loadMenu, findItemByCode
} from '../store/menuSlice';
import { openModal, closeModal, selectModal, incrementFiscalPending, loadFiscalStatus } from '../store/uiSlice';
import { orderApi, menuApi } from '../services/endpoints';
import { printReceipt, printKitchenTicket } from '../components/print/printBus';
import useHotkeys from '../hooks/useHotkeys';
import useBarcodeScanner from '../hooks/useBarcodeScanner';
import { money } from '../utils/format';

const PosPage = () => {
    const dispatch = useDispatch();

    const cart = useSelector(selectCart);
    const totals = useSelector(selectTotals);
    const orderPayload = useSelector(selectOrderPayload);
    const { search, settings, viewMode, items: menuItems } = useSelector(selectMenu);
    const orderTypes = useSelector(selectOrderTypes);
    const paymentModes = useSelector(selectPaymentModes);
    const { modal, payload: modalPayload } = useSelector(selectModal);

    const [pendingItem, setPendingItem] = useState(null);
    const [pendingVariantId, setPendingVariantId] = useState(null);
    const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
    const [preferredMode, setPreferredMode] = useState(null);
    const [busy, setBusy] = useState(false);

    const searchRef = useRef(null);

    /* ------------------------------ adding items --------------------------- */

    const addToCart = useCallback((choice) => {
        const { item, variantId, variantName, unitRate, quantity, itemNote, modifiers } = choice;

        dispatch(addItem({
            menuItemId: item.menuItemId,
            variantId,
            itemName: item.name,
            variantName,
            unitRate,
            quantity,
            itemNote,
            modifiers,
            taxPercent: item.taxPercent,
            priceIncludesTax: item.priceIncludesTax,
            maxDiscountPercent: item.maxDiscountPercent,
            uom: item.uom,
            kitchenSection: item.kitchenSection,
            isDeal: item.isDeal,
            imagePath: item.imagePath
        }));

        setPendingItem(null);
    }, [dispatch]);

    /**
     * An item tapped, or scanned. `variant` is set only when a size was scanned
     * by its own barcode: that size is decided, so the dialog is needed only if
     * the item also has options to choose.
     */
    const onSelectItem = useCallback((item, variant = null) => {
        // A sold-out tile cannot be tapped, but its barcode can still be scanned.
        if (!item.isAvailable) {
            toast.error(`${item.name} is sold out.`, { id: 'scan' });
            return;
        }

        if (variant ? item.modifierGroups?.length > 0 : itemNeedsDialog(item)) {
            setPendingVariantId(variant?.variantId ?? null);
            setPendingItem(item);
            return;
        }
        addToCart({
            item,
            variantId: variant?.variantId ?? null,
            variantName: variant?.name ?? null,
            unitRate: variant ? variant.price : item.price,
            quantity: 1,
            itemNote: null,
            modifiers: []
        });
    }, [addToCart]);

    /**
     * Put whatever a barcode or short code belongs to on the bill.
     *
     * The menu in memory is checked first: it is instant, and it carries the
     * sizes and options the item may need to ask about. The server is asked only
     * when that misses - the item was added or re-coded after the till loaded
     * its menu - and the menu is then reloaded so the item arrives complete.
     *
     * Resolves false when nothing has the code; rejects on any other failure.
     */
    const addByCode = useCallback(async (code) => {
        const local = findItemByCode(menuItems, code);
        if (local) {
            onSelectItem(local.item, local.variant);
            return true;
        }

        let found;
        try {
            found = await menuApi.lookupBarcode(code);
        } catch (err) {
            if (err.status === 404) return false;
            throw err;
        }

        const menu = await dispatch(loadMenu()).unwrap();
        const item = menu.items.find((i) => i.menuItemId === found.Menu_Item_ID);
        if (!item) return false;

        onSelectItem(item, item.variants.find((v) => v.variantId === found.Scanned_Variant_ID) || null);
        return true;
    }, [dispatch, menuItems, onSelectItem]);

    /**
     * The barcode scanner, wherever focus was.
     *
     * Refused while a dialog is open: focus is most likely in the payment
     * dialog's amount box, and the bill should not change while it is being paid.
     */
    useBarcodeScanner(async (code) => {
        if (modal || pendingItem) {
            toast.error('Close the open dialog before scanning.', { id: 'scan' });
            return;
        }
        try {
            if (!(await addByCode(code))) {
                toast.error(`Nothing on the menu has the barcode ${code}.`, { id: 'scan' });
            }
        } catch (err) {
            toast.error(err.message, { id: 'scan' });
        }
    });

    // Typing a code and pressing Enter does the same, for a label that will not
    // scan. Anything that is not a code stays a name search.
    const onSearchKeyDown = async (event) => {
        if (event.key !== 'Enter' || !search.trim()) return;
        try {
            if (await addByCode(search.trim())) dispatch(setSearch(''));
        } catch (err) {
            toast.error(err.message);
        }
    };

    /* -------------------------------- saving ------------------------------- */

    const saveOrder = useCallback(async () => {
        dispatch(setSaving(true));
        try {
            const detail = await orderApi.save(orderPayload);
            dispatch(loadFromOrder(detail));
            return detail;
        } finally {
            dispatch(setSaving(false));
        }
    }, [dispatch, orderPayload]);

    /* ------------------------------- actions ------------------------------- */

    const onSendToKitchen = async () => {
        if (!cart.lines.length) return;
        setBusy(true);
        try {
            const detail = await saveOrder();
            const result = await orderApi.sendToKitchen(detail.order.Order_ID);
            for (const ticket of result.tickets || []) {
                await printKitchenTicket(ticket);
            }
            const refreshed = await orderApi.get(detail.order.Order_ID);
            dispatch(loadFromOrder(refreshed));
            toast.success(result.message);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusy(false);
        }
    };

    const onHold = async (label) => {
        setBusy(true);
        try {
            const detail = await saveOrder();
            await orderApi.hold(detail.order.Order_ID, label);
            toast.success(`Held as "${label || detail.order.Order_Number}".`);
            dispatch(clearCart());
            dispatch(closeModal());
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusy(false);
        }
    };

    const onRecall = async (orderId) => {
        try {
            const detail = await orderApi.get(orderId);
            dispatch(loadFromOrder(detail));
            dispatch(closeModal());
            toast.success(`Recalled ${detail.order.Order_Number}.`);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const onPay = async (mode) => {
        if (!cart.lines.length) return;
        setBusy(true);
        try {
            await saveOrder();
            setPreferredMode(mode);
            dispatch(openModal({ modal: 'payment' }));
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusy(false);
        }
    };

    const onConfirmPayment = async (payments) => {
        setBusy(true);
        try {
            const result = await orderApi.settle(cart.orderId, { payments });
            if (settings.AUTO_PRINT_RECEIPT !== false && result.receipt) {
                await printReceipt(result.receipt, {
                    copies: result.receipt.template?.printCopies || 1
                });
                await orderApi.confirmPrint(cart.orderId).catch(() => { });
            }
            const change = Number(result.order.Change_Amount) || 0;
            toast.success(
                change > 0
                    ? `Paid. Change Rs. ${money(change)}`
                    : `Paid. Invoice ${result.order.Invoice_Number}`,
                { duration: change > 0 ? 6000 : 3000 }
            );
            dispatch(incrementFiscalPending());
            dispatch(closeModal());
            dispatch(clearCart());
            setTimeout(() => dispatch(loadFiscalStatus()), 3000);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusy(false);
        }
    };

    /* ------------------------------- shortcuts ----------------------------- */

    useHotkeys({
        F1: () => toast('F2 hold · F3 recall · F4 tables · F5 customer · F6 pay · Esc clear search',
            { icon: '⌨️', duration: 6000 }),
        F2: () => cart.lines.length && dispatch(openModal({ modal: 'hold' })),
        F3: () => dispatch(openModal({ modal: 'recall' })),
        F4: () => dispatch(openModal({ modal: 'table' })),
        F5: () => dispatch(openModal({ modal: 'customer' })),
        F6: () => cart.lines.length && onPay(null),
        Escape: () => { if (search) dispatch(setSearch('')); },
        'Ctrl+F': () => searchRef.current?.focus()
    });

    useEffect(() => { searchRef.current?.focus(); }, []);

    const activeOrderType = orderTypes.find((t) => t.Order_Type_Code === cart.orderTypeCode);

    return (
        <div className="flex h-full gap-1 overflow-hidden p-1.5">

            {/* ============================================================
                LEFT — menu panel
                ============================================================ */}
            <div className="flex min-w-0 flex-1  flex-col gap-1">

                {/* ── Toolbar ─────────────────────────────────────────── */}
                <div
                    className="flex flex-col gap-1.5 rounded-xl px-2 py-1.5 shadow-sm"
                    style={{
                        background: 'linear-gradient(135deg, #05650fff 0%, #158f21ff 100%)',
                    }}
                >
                    {/* Chips and order type share a row from `sm`. On a phone
                        they cannot: three order types need most of the width on
                        their own, and sharing left the chips squashed under
                        them. So each takes a full-width row of its own. */}
                    <div className="flex flex-wrap items-center justify-between gap-1.5 sm:gap-2">
                        <div className="flex w-full min-w-0 gap-2 sm:w-auto sm:flex-1">
                            {/* Customer chip */}
                            <button
                                type="button"
                                onClick={() => dispatch(openModal({ modal: 'customer' }))}
                                className="flex h-11 min-w-0 items-center gap-1.5 rounded-lg border border-white/20
                                   bg-white/10 px-2.5 text-xs font-semibold text-white/80
                                   transition-all duration-150 hover:bg-white/20 hover:text-white lg:h-8 lg:px-3"
                            >
                                <FiUser className="h-3.5 w-3.5 shrink-0" />
                                <span className="max-w-[6rem] truncate sm:max-w-[10rem]">{cart.customerName}</span>
                                <FiPlus className="h-3.5 w-3.5 shrink-0 opacity-60" />
                            </button>
                            {/* Table chip (dine-in only) */}
                            {activeOrderType?.Requires_Table && (
                                <button
                                    type="button"
                                    onClick={() => dispatch(openModal({ modal: 'table' }))}
                                    className={`flex h-11 shrink-0 items-center gap-1.5 rounded-lg border px-3 text-xs
                                        font-semibold transition-all duration-150 lg:h-8 lg:px-5
                                        ${cart.tableId
                                            ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30'
                                            : 'border-amber-400/60 bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'}`}
                                >
                                    <FiUsers className="h-3.5 w-3.5 shrink-0" />
                                    <span className="whitespace-nowrap">
                                        {cart.tableNumber ? `Table ${cart.tableNumber}` : 'Choose table'}
                                    </span>
                                    <FiChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
                                </button>
                            )}
                        </div>

                        {/* Order type pills. Dine-in / take-away / delivery is
                            three names of unpredictable length; on a narrow
                            screen the group scrolls rather than shrinking each
                            one into an unreadable stub. */}
                        <div className="flex w-full max-w-full shrink-0 overflow-x-auto scrollbar-none
                                        rounded-lg border border-white/20 bg-white/10 sm:w-auto">
                            {orderTypes.map((type) => (
                                <button
                                    key={type.Order_Type_ID}
                                    type="button"
                                    onClick={() => dispatch(setOrderType(type.Order_Type_Code))}
                                    className={`flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap px-2.5
                                            text-xs font-semibold tracking-wide transition-all duration-150
                                            lg:h-8 lg:px-1.5
                                            ${cart.orderTypeCode === type.Order_Type_Code
                                            ? 'bg-white text-brand-700 shadow-sm'
                                            : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                                >
                                    {type.Order_Type_Name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">

                        {/* Search. The `relative` here is load-bearing: the
                            magnifier and the clear button are positioned
                            against this box, and without it they anchored to
                            the page instead and sat in the wrong place. */}
                        <div className="relative min-w-0 flex-1">
                            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
                            <input
                                ref={searchRef}
                                className="h-11 w-full rounded-lg border border-white/20 bg-white/10 pl-9 pr-8
                                       text-sm text-white placeholder:text-white/40 lg:h-8
                                       focus:border-white/50 focus:bg-white/15 focus:outline-none focus:ring-0
                                       transition-all backdrop-blur-sm"
                                placeholder="Search items or scan barcode…"
                                value={search}
                                onChange={(e) => dispatch(setSearch(e.target.value))}
                                onKeyDown={onSearchKeyDown}
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => dispatch(setSearch(''))}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                                    aria-label="Clear search"
                                >
                                    <FiX className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                        {/* Grid / List toggle */}
                        <div className="flex shrink-0 overflow-hidden rounded-lg border border-white/20 bg-white/10">
                            <button
                                type="button"
                                onClick={() => dispatch(setViewMode('grid'))}
                                className={`flex h-11 w-11 items-center justify-center transition-all duration-150
                                        lg:h-8 lg:w-8
                                        ${viewMode === 'grid'
                                        ? 'bg-white text-brand-700 shadow-sm'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                                aria-label="Grid view"
                            >
                                <FiGrid className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => dispatch(setViewMode('list'))}
                                className={`flex h-11 w-11 items-center justify-center border-l border-white/20
                                        transition-all duration-150 lg:h-8 lg:w-8
                                        ${viewMode === 'list'
                                        ? 'bg-white text-brand-700 shadow-sm'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                                aria-label="List view"
                            >
                                <FiList className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                </div>

                {/* ── Category strip ──────────────────────────────────── */}
                <div className="rounded-xl bg-white px-2 py-1 shadow-sm border border-slate-200/80">
                    <CategoryTabs />
                </div>

                {/* ── Item grid ───────────────────────────────────────
                    The bottom padding clears the cart bar that is fixed over
                    this region below `lg`, plus the home indicator under it.
                    Without it the last row of tiles cannot be tapped. */}
                <div className="min-h-[12rem] flex-1 overflow-y-auto pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] pr-0.5 lg:pb-0">
                    <ItemGrid onSelect={onSelectItem} />
                </div>

                {/* ── Function-key strip ──────────────────────────────── */}
                <div className="hidden shrink-0 rounded-xl border border-slate-200 bg-white px-1 py-1
                                shadow-sm lg:flex flex-wrap items-center gap-1">
                    {[
                        { key: 'F1', label: 'Help', icon: FiHelpCircle, action: () => toast('F2 hold · F3 recall · F4 tables · F5 customer · F6 pay') },
                        { key: 'F2', label: 'Hold', icon: FiPause, action: () => cart.lines.length && dispatch(openModal({ modal: 'hold' })) },
                        { key: 'F3', label: 'Recall', icon: FiClock, action: () => dispatch(openModal({ modal: 'recall' })) },
                        { key: 'F4', label: 'Tables', icon: FiGrid, action: () => dispatch(openModal({ modal: 'table' })) },
                        { key: 'F5', label: 'Customer', icon: FiUser, action: () => dispatch(openModal({ modal: 'customer' })) },
                        { key: 'F6', label: 'Quick Pay', icon: FiPrinter, action: () => cart.lines.length && onPay(null) },
                    ].map(({ key, label, icon: Icon, action }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={action}
                            className="flex items-center gap-1 rounded-lg px-1 py-1
                                       text-xs font-medium text-slate-500
                                       hover:bg-brand-50 hover:text-brand-700
                                       transition-colors duration-100 group"
                        >
                            <kbd className="rounded-md border border-slate-300 bg-slate-50 px-1.5 py-0.5
                                           font-mono text-[10px] font-bold text-slate-600
                                           group-hover:border-brand-300 group-hover:bg-brand-50 group-hover:text-brand-700
                                           transition-colors">
                                {key}
                            </kbd>
                            <Icon className="h-3 w-3" />
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ============================================================
                RIGHT — order panel
                ============================================================ */}
            <div className="hidden w-[22rem] shrink-0 lg:block xl:w-[26rem] 2xl:w-[28rem]">
                <OrderPanel onPay={onPay} onSendToKitchen={onSendToKitchen} saving={busy || cart.isSaving} />
            </div>

            {/* ── Cart bar, below lg ──────────────────────────────────
                Below `lg` there is not enough width for the grid and the order
                panel at once, so the bill collapses to this bar and opens as a
                sheet. It shows the two things a cashier checks without opening
                anything - how many lines, and what it comes to. */}
            <button
                type="button"
                onClick={() => setOrderDrawerOpen(true)}
                className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3
                           px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]
                           text-white shadow-panel lg:hidden"
                style={{ background: 'linear-gradient(135deg, #0b1a5c 0%, #152fe1 100%)' }}
            >
                <span className="flex items-center gap-2 text-sm font-medium">
                    <FiShoppingCart className="h-5 w-5" />
                    {cart.lines.length} item{cart.lines.length === 1 ? '' : 's'}
                </span>
                <span className="tabular text-lg font-bold">
                    Rs. {money(totals.totalPayable)}
                </span>
            </button>

            {orderDrawerOpen && (
                <div className="fixed inset-0 z-40 flex lg:hidden">
                    <div
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
                        onClick={() => setOrderDrawerOpen(false)}
                    />

                    {/* On a phone the sheet takes the screen: the bill is the
                        task at that point, and a half-width panel would put the
                        Pay button back below the fold. On a tablet it is a
                        panel down the right, which leaves the grid visible so
                        another item can be added without closing anything. */}
                    <div className="relative ml-auto flex h-full w-full flex-col animate-slide-in-right
                                    p-0 sm:w-[26rem] sm:p-2">
                        <OrderPanel
                            onPay={(mode) => { setOrderDrawerOpen(false); onPay(mode); }}
                            onSendToKitchen={() => { setOrderDrawerOpen(false); onSendToKitchen(); }}
                            saving={busy || cart.isSaving}
                            onClose={() => setOrderDrawerOpen(false)}
                        />
                    </div>
                </div>
            )}

            {/* ── Dialogs ─────────────────────────────────────────────── */}
            <ModifierModal
                open={!!pendingItem}
                item={pendingItem}
                initialVariantId={pendingVariantId}
                onClose={() => setPendingItem(null)}
                onConfirm={addToCart}
            />

            <PaymentModal
                open={modal === 'payment'}
                onClose={() => dispatch(closeModal())}
                onConfirm={onConfirmPayment}
                totalPayable={totals.totalPayable}
                paymentModes={paymentModes}
                preferredMode={preferredMode}
                busy={busy}
            />

            <HoldDialog
                open={modal === 'hold'}
                onClose={() => dispatch(closeModal())}
                onHold={onHold}
                busy={busy}
            />

            <RecallDialog
                open={modal === 'recall'}
                onClose={() => dispatch(closeModal())}
                onRecall={onRecall}
            />

            <DiscountDialog open={modal === 'discount'} onClose={() => dispatch(closeModal())} />
            <NoteDialog open={modal === 'note'} onClose={() => dispatch(closeModal())} />
            <CustomerDialog open={modal === 'customer'} onClose={() => dispatch(closeModal())} />
            <TableDialog open={modal === 'table'} onClose={() => dispatch(closeModal())} />

            <LineOptionsDialog
                open={modal === 'lineOptions'}
                line={modalPayload}
                onClose={() => dispatch(closeModal())}
            />
        </div>
    );
};

export default PosPage;
